import { JitsiConference, JitsiMeet, JitsiTrack } from "lib/lib-jitsi-meet";
import { action, IObservableArray, observable } from "mobx";
import { PerformerIdMessage, SpotlightUserMessage } from "src/chat";
import { getDefaultRemoteVideoLayoutOptions, LocalUserState, PerformanceState, RoomState } from "../../common";
import AbstractDataComponent from "./AbstractDataComponent";
import { JitsiManagerChildProps } from "./JitsiManager";

export interface JitsiConferenceManagerProps extends JitsiManagerChildProps {
    roomState: RoomState;
    localUser: LocalUserState;
    performanceState?: PerformanceState;
}

export default class extends AbstractDataComponent<JitsiConferenceManagerProps> {
    // TODO: handle this with reactions
    public componentDidMount() {
        this.bindRoomHandlers(this.props.jitsi, this.props.room);
    }

    public componentDidUpdate(prevProps: JitsiConferenceManagerProps) {
        if (prevProps.room !== this.props.room) {
            this.unbindRoomHandlers(this.props.jitsi, prevProps.room);
            this.bindRoomHandlers(this.props.jitsi, this.props.room);
        }
    }

    public componentWillUnmount() {
        this.unbindRoomHandlers(this.props.jitsi, this.props.room);
    }

    public bindRoomHandlers(jitsi: JitsiMeet, room: JitsiConference) {
        room.on(jitsi.events.conference.TRACK_ADDED, this.handleRoomTrackAdded);
        room.on(jitsi.events.conference.TRACK_REMOVED, this.handleRoomTrackRemoved);
        room.on(jitsi.events.conference.CONFERENCE_JOINED, this.handleConferenceJoined);
        room.on(jitsi.events.conference.USER_JOINED, this.handleUserJoined);
        room.on(jitsi.events.conference.USER_LEFT, this.handleUserLeft);
        room.on(jitsi.events.conference.DISPLAY_NAME_CHANGED, this.handleDisplayNameChanged);
    }

    public unbindRoomHandlers(jitsi: JitsiMeet, room: JitsiConference) {
        room.off(jitsi.events.conference.TRACK_ADDED, this.handleRoomTrackAdded);
        room.off(jitsi.events.conference.CONFERENCE_JOINED, this.handleConferenceJoined);
        room.off(jitsi.events.conference.USER_JOINED, this.handleUserJoined);
        room.off(jitsi.events.conference.USER_LEFT, this.handleUserLeft);
        room.off(jitsi.events.conference.DISPLAY_NAME_CHANGED, this.handleDisplayNameChanged);
    }

    @action
    private handleRoomTrackAdded = (track: JitsiTrack) => {
        if (track.isLocal()) {
            return;
        }

        const { roomState } = this.props;

        const userId = track.getParticipantId();
        const existingParticipantTracks = roomState.tracks.get(userId);

        if (existingParticipantTracks === undefined) {
            roomState.tracks.set(userId, observable([track]));
        } else {
            existingParticipantTracks.push(track);
        }

        this.maybeBroadcastPerformerId(userId);
        this.maybeBroadcastSpotlight(userId);
    };

    @action
    private handleRoomTrackRemoved = (track: JitsiTrack) => {
        if (track.isLocal()) {
            return;
        }

        const participant = track.getParticipantId();
        const existingParticipantTracks = this.props.roomState.tracks.get(participant);
        if (existingParticipantTracks === undefined) {
            console.error("Trying to remove a remote track which we were not tracking locally!");
        } else {
            // remove the track from our local state
            (existingParticipantTracks as IObservableArray<JitsiTrack>).remove(track);
        }
    };

    @action
    private handleConferenceJoined = () => {
        const { roomState, room, localUser, performanceState } = this.props;

        roomState.roomJoined = "joined";

        // if we re-joined, we might already have a display name which we need to notify the room about
        if (localUser.displayName !== undefined) {
            room.setDisplayName(localUser.displayName);
        }

        if (localUser.performerId !== undefined && performanceState !== undefined) {
            console.log("setting self user layout", localUser.performerId, room.myUserId());
            performanceState.userLayout.set(localUser.performerId, room.myUserId());
        }
    };

    @action
    private handleUserJoined = (userId: string) => {
        const { roomState, roomName } = this.props;
        roomState.videoLayout.push(getDefaultRemoteVideoLayoutOptions(roomName, userId));
        this.maybeBroadcastPerformerId(userId);
        this.maybeBroadcastSpotlight(userId);
    };

    @action
    private handleDisplayNameChanged = (userId: string, displayName: string) => {
        this.props.roomState.displayNames.set(userId, displayName);
    };

    @action
    private handleUserLeft = (userId: string) => {
        const { roomState } = this.props;
        const user = roomState.videoLayout.find((info) => info.userId === userId);
        if (user !== undefined) {
            roomState.videoLayout.remove(user);
        }
    };

    private maybeBroadcastPerformerId = (toUser: string) => {
        const { room, localUser } = this.props;

        if (localUser.performerId !== undefined) {
            const performerIdMessage: PerformerIdMessage = {
                kind: "performerId",
                userId: room.myUserId(),
                id: localUser.performerId,
            };
            room.sendMessage(performerIdMessage, toUser);
        }
    };

    private maybeBroadcastSpotlight = (toUser: string) => {
        const { room, roomState } = this.props;

        if (roomState.spotlightUserId != null) {
            const message: SpotlightUserMessage = {
                kind: "spotlightUser",
                userId: roomState.spotlightUserId,
            };
            room.sendMessage(message, toUser);
        }
    };
}
